import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { useStableUrls } from "../hooks/useStableUrl";
import { usePinchZoom } from "../hooks/usePinchZoom";
import { useI18n } from "../i18n";

export interface ImageData {
  _id: Id<"images">;
  url: string | null;
  referenceUrls?: string[];
  referenceUrl: string | null;
  prompt: string;
  status: "pending" | "generating" | "completed" | "failed";
  createdAt: number;
  generationStartedAt?: number;
  generationCompletedAt?: number;
  generationDurationMs?: number;
  imageSizeBytes?: number;
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
  parentImageId?: Id<"images">;
  editInstruction?: string;
}

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: ImageData | null;
  onOpenEditModal?: (image: ImageData) => void;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(0);
  return `${minutes}m ${remainingSeconds}s`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(timestamp: number, locale: string): string {
  return new Date(timestamp).toLocaleString(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

// Gemini pricing (approximate, as of 2024)
// For image generation, pricing is typically per image or per 1K tokens
function estimateCost(inputTokens?: number, outputTokens?: number): string | null {
  if (!inputTokens && !outputTokens) return null;

  // Gemini Flash pricing is approximately:
  // Input: $0.075 per 1M tokens, Output: $0.30 per 1M tokens
  // Image generation may have different pricing
  const inputCost = (inputTokens || 0) * 0.000000075;
  const outputCost = (outputTokens || 0) * 0.0000003;
  const total = inputCost + outputCost;

  if (total < 0.0001) return "< $0.0001";
  if (total < 0.01) return `~$${total.toFixed(4)}`;
  return `~$${total.toFixed(2)}`;
}

export default function ImageModal({ isOpen, onClose, image, onOpenEditModal }: ImageModalProps) {
  const { t, locale } = useI18n();
  const [fullscreenRefIndex, setFullscreenRefIndex] = useState<number | null>(null);
  const [showFullscreenMain, setShowFullscreenMain] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(image?._id);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const removeImage = useMutation(api.images.remove);
  const mainZoom = usePinchZoom();
  const refZoom = usePinchZoom();

  const handleDownload = async (imageUrl: string, imageId: string) => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const filename = `generated-${imageId}.png`;

    // On iOS, use Web Share API if available for better UX (allows "Save Image")
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && navigator.share && navigator.canShare) {
      const file = new File([blob], filename, { type: "image/png" });
      const shareData = { files: [file] };

      if (navigator.canShare(shareData)) {
        try {
          await navigator.share(shareData);
          return;
        } catch (err) {
          // User cancelled or share failed, fall through to download
          if ((err as Error).name === "AbortError") return;
        }
      }
    }

    // Fallback: standard download
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  };

  // Query edit history for this image
  const rawEditHistory = useQuery(
    api.images.getEditHistory,
    image?._id ? { imageId: image._id } : "skip"
  );
  // Stabilize URLs to prevent constant image reloading
  const editHistory = useStableUrls(rawEditHistory);

  // Get the currently selected image from history
  const selectedImage = editHistory?.find(img => img._id === selectedImageId);
  const displayImage = selectedImage || image;

  // Reset selected image when modal opens with new image
  useEffect(() => {
    if (image) {
      setSelectedImageId(image._id);
    }
  }, [image?._id]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (fullscreenRefIndex !== null) {
        setFullscreenRefIndex(null);
      } else if (showFullscreenMain) {
        setShowFullscreenMain(false);
      } else {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, fullscreenRefIndex, showFullscreenMain]);

  if (!isOpen || !image) return null;

  const estimatedCost = estimateCost(image.inputTokens, image.outputTokens);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full h-full md:w-[95vw] md:h-[90vh] md:max-w-7xl bg-white md:rounded-softer shadow-2xl flex flex-col md:flex-row overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:top-4 md:right-4 z-10 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-charcoal hover:bg-coral-400 hover:text-white transition-colors shadow-soft"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Image section - top on mobile, left on desktop */}
        <div className="flex-1 bg-linen-100 flex items-center justify-center p-4 md:p-8 min-h-[40vh] md:min-h-0">
          {displayImage?.url ? (
            <img
              src={displayImage.url}
              alt={displayImage.prompt}
              onClick={() => setShowFullscreenMain(true)}
              className="max-w-full max-h-full object-contain rounded-soft shadow-card cursor-pointer hover:opacity-90 transition-opacity"
            />
          ) : (
            <div className="text-charcoal-muted">{t.common.noImageAvailable}</div>
          )}
        </div>

        {/* Details section - bottom on mobile, right on desktop */}
        <div className="w-full md:w-[340px] lg:w-[380px] bg-white border-t md:border-t-0 md:border-l border-linen-200 p-4 md:p-6 overflow-y-auto flex flex-col max-h-[55vh] md:max-h-none">
          {/* Header with title and action buttons */}
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-xl md:text-2xl font-serif text-charcoal">{t.imageModal.title}</h2>

            {/* Compact action buttons */}
            {image.url && (
              <div className="flex items-center gap-1.5 mr-10">
                {onOpenEditModal && (
                  <button
                    onClick={() => onOpenEditModal(image)}
                    className="group relative w-8 h-8 bg-coral-50 text-coral-600 rounded-lg flex items-center justify-center hover:bg-coral-500 hover:text-white transition-all duration-200 hover:shadow-md"
                    aria-label={t.common.edit}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {/* Tooltip */}
                    <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 text-xs font-medium text-white bg-charcoal rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-20">
                      {t.common.edit}
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-charcoal"></span>
                    </span>
                  </button>
                )}
                <button
                  onClick={() => {
                    const url = displayImage?.url || image.url;
                    const id = displayImage?._id || image._id;
                    if (url) handleDownload(url, id);
                  }}
                  className="group relative w-8 h-8 bg-sage-50 text-sage-600 rounded-lg flex items-center justify-center hover:bg-sage-500 hover:text-white transition-all duration-200 hover:shadow-md"
                  aria-label={t.common.download}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {/* Tooltip */}
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 text-xs font-medium text-white bg-charcoal rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-20">
                    {t.common.download}
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-charcoal"></span>
                  </span>
                </button>
                {/* Delete button */}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="group relative w-8 h-8 bg-red-50 text-red-500 rounded-lg flex items-center justify-center hover:bg-red-500 hover:text-white transition-all duration-200 hover:shadow-md"
                  aria-label={t.common.delete}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  {/* Tooltip */}
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 text-xs font-medium text-white bg-charcoal rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-20">
                    {t.common.delete}
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-charcoal"></span>
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Edit history - only show if there are edits (more than 1 item in history) */}
          {editHistory && editHistory.length > 1 && (
            <div className="mb-4 md:mb-6">
              <h3 className="text-xs md:text-sm font-medium text-charcoal-muted uppercase tracking-wide mb-2 md:mb-3">
                {t.imageModal.editHistory}
              </h3>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {editHistory.map((historyItem, index) => (
                  <button
                    key={historyItem._id}
                    onClick={() => setSelectedImageId(historyItem._id)}
                    className={`relative flex-shrink-0 w-16 h-16 rounded-soft overflow-hidden border-2 transition-colors ${
                      selectedImageId === historyItem._id
                        ? "border-coral-500"
                        : "border-linen-200 hover:border-linen-300"
                    }`}
                  >
                    {historyItem.url ? (
                      <img
                        src={historyItem.url}
                        alt={historyItem.editInstruction || historyItem.prompt}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-linen-100 flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-charcoal-muted/30 rounded-full border-t-transparent animate-spin" />
                      </div>
                    )}
                    {index === 0 && (
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5">
                        {t.common.original}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Edit instruction (if this is an edit) */}
          {selectedImage?.editInstruction && (
            <div className="mb-4 md:mb-6">
              <h3 className="text-xs md:text-sm font-medium text-charcoal-muted uppercase tracking-wide mb-2">
                {t.imageModal.editApplied}
              </h3>
              <p className="text-charcoal bg-coral-50 p-3 md:p-4 rounded-soft text-sm leading-relaxed">
                {selectedImage.editInstruction}
              </p>
            </div>
          )}

          {/* Prompt */}
          <div className="mb-4 md:mb-6">
            <h3 className="text-xs md:text-sm font-medium text-charcoal-muted uppercase tracking-wide mb-2">
              {t.imageModal.prompt}
            </h3>
            <p className="text-charcoal bg-linen-50 p-3 md:p-4 rounded-soft text-sm leading-relaxed">
              {image.prompt}
            </p>
          </div>

          {/* Reference Images (if used) - show from original image in edit chain */}
          {(() => {
            const refUrls = editHistory?.[0]?.referenceUrls ?? image.referenceUrls ?? (image.referenceUrl ? [image.referenceUrl] : []);
            if (refUrls.length === 0) return null;
            return (
              <div className="mb-4 md:mb-6">
                <h3 className="text-xs md:text-sm font-medium text-charcoal-muted uppercase tracking-wide mb-2">
                  {refUrls.length === 1
                    ? t.imageModal.referenceImage
                    : t.imageModal.referenceImages(refUrls.length)}
                </h3>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {refUrls.map((url, index) => (
                    <img
                      key={url}
                      src={url}
                      alt={t.sidebar.referenceAlt(index + 1)}
                      onClick={() => setFullscreenRefIndex(index)}
                      className="max-h-28 md:max-h-36 object-contain rounded-soft border border-linen-200 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                    />
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Generation Stats - Compact inline */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-charcoal-muted mb-4 md:mb-6">
            {/* Duration */}
            {image.generationDurationMs !== undefined && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {formatDuration(image.generationDurationMs)}
              </span>
            )}

            {/* Estimated Cost */}
            {estimatedCost && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {estimatedCost}
              </span>
            )}

            {/* File Size */}
            {image.imageSizeBytes !== undefined && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatFileSize(image.imageSizeBytes)}
              </span>
            )}

            {/* Model */}
            {image.model && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {image.model.replace("gemini-", "").replace("-exp-image-generation", "")}
              </span>
            )}

            {/* Created */}
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formatDate(image.createdAt, locale)}
            </span>
          </div>

        </div>
      </div>

      {/* Fullscreen Main Image Overlay */}
      {showFullscreenMain && displayImage?.url && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 overflow-hidden"
          onClick={(e) => {
            e.stopPropagation();
            mainZoom.reset();
            setShowFullscreenMain(false);
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              mainZoom.reset();
              setShowFullscreenMain(false);
            }}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-charcoal hover:bg-coral-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {/* Zoom hint */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs md:hidden">
            {t.common.zoomHint}
          </div>
          <img
            src={displayImage.url}
            alt={displayImage.prompt}
            className="max-w-[90vw] max-h-[90vh] object-contain select-none"
            style={mainZoom.style}
            onClick={(e) => e.stopPropagation()}
            {...mainZoom.handlers}
          />
        </div>
      )}

      {/* Fullscreen Reference Image Overlay */}
      {fullscreenRefIndex !== null && (() => {
        const refUrls = editHistory?.[0]?.referenceUrls ?? image.referenceUrls ?? (image.referenceUrl ? [image.referenceUrl] : []);
        const currentRefUrl = refUrls[fullscreenRefIndex];
        if (!currentRefUrl) return null;
        return (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 overflow-hidden"
            onClick={(e) => {
              e.stopPropagation();
              refZoom.reset();
              setFullscreenRefIndex(null);
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                refZoom.reset();
                setFullscreenRefIndex(null);
              }}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-charcoal hover:bg-coral-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {/* Prev/Next navigation for multiple references */}
            {refUrls.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    refZoom.reset();
                    setFullscreenRefIndex((fullscreenRefIndex - 1 + refUrls.length) % refUrls.length);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-charcoal hover:bg-white transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    refZoom.reset();
                    setFullscreenRefIndex((fullscreenRefIndex + 1) % refUrls.length);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-charcoal hover:bg-white transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                {/* Counter */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/50 px-3 py-1 rounded-full">
                  {fullscreenRefIndex + 1} / {refUrls.length}
                </div>
              </>
            )}
            {/* Zoom hint */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs md:hidden">
              Pinch to zoom • Double-tap to reset
            </div>
            <img
              src={currentRefUrl}
              alt={t.sidebar.referenceAlt(fullscreenRefIndex + 1)}
              className="max-w-[90vw] max-h-[90vh] object-contain select-none"
              style={refZoom.style}
              onClick={(e) => e.stopPropagation()}
              {...refZoom.handlers}
            />
          </div>
        );
      })()}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteConfirm(false);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium text-charcoal mb-2">{t.common.deleteImageTitle}</h3>
            <p className="text-sm text-charcoal-muted mb-4">
              {editHistory && editHistory.length > 1 && selectedImageId !== editHistory[0]?._id
                ? t.common.deleteVersionMessage
                : t.common.deletePermanentMessage}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-charcoal hover:bg-linen-100 rounded-lg transition-colors"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={async () => {
                  const idToDelete = selectedImageId || image._id;
                  await removeImage({ imageId: idToDelete });
                  setShowDeleteConfirm(false);
                  // If we deleted the current image or it's a single image, close modal
                  if (!editHistory || editHistory.length <= 1 || selectedImageId === image._id) {
                    onClose();
                  } else {
                    // Switch to another image in history
                    const remaining = editHistory?.filter(h => h._id !== idToDelete) || [];
                    const lastRemaining = remaining[remaining.length - 1];
                    if (lastRemaining) {
                      setSelectedImageId(lastRemaining._id);
                    } else {
                      onClose();
                    }
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                {t.common.delete}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
